import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { useParams, useHistory, Link } from 'react-router-dom'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  BugReport as BugReportIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
} from '@material-ui/icons'
import dayjs from 'dayjs'
import { colors } from '../../../../Themes/Colors'
import {
  UserAdmin,
  useLoginAsMutation,
} from '../../../../Generated/graphql-types'
import GET_USER_BY_ID from '../../../../Apollo/Queries/GetUserById'
import {
  PageContainer,
  HeaderSection,
  BackButton,
  TitleSection,
  Title,
  Subtitle,
  StatusChip,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  SectionTitle,
  InfoCard,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  ScrollablePanel,
  EmptyState,
  ActionBar,
  LoadingContainer,
  ErrorContainer,
} from './UserDetail.styled'

interface RouteParams {
  id: string
}

export const UserDetail: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const userId = parseInt(id, 10)
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false)

  const { data, loading, error } = useQuery<{ getUserById: UserAdmin }>(
    GET_USER_BY_ID,
    {
      variables: { id: userId },
      skip: !userId,
    }
  )

  const [loginAs, { loading: impersonateLoading }] = useLoginAsMutation({
    onCompleted: (mutationData) => {
      // eslint-disable-next-line no-console
      console.log('Impersonation successful:', mutationData)
      // Reload the page after successful impersonation
      // Force a hard reload to ensure all cached data is cleared
      // eslint-disable-next-line no-restricted-globals
      window.location.reload()
    },
    onError: (mutationError) => {
      // TODO: Add proper error handling with toast notification
      // eslint-disable-next-line no-console
      console.error('Failed to impersonate user:', mutationError)
    },
  })

  const user = data?.getUserById

  const handleBack = () => {
    history.push('/internal/self-serve/user-admin')
  }

  const handleImpersonate = () => {
    setShowImpersonateDialog(true)
  }

  const confirmImpersonate = () => {
    setShowImpersonateDialog(false)
    localStorage.setItem('ImpersonationSession', 'yes')
    loginAs({ variables: { userId } })
  }

  const navigateToFunders = () => {
    // Navigate to funder admin with user email filter
    history.push(
      `/internal/self-serve/funder-admin?userEmail=${encodeURIComponent(
        user?.email || ''
      )}`
    )
  }

  const navigateToNonprofits = () => {
    // Navigate to nonprofit admin with user email filter
    history.push(
      `/internal/self-serve/nonprofit-admin?userEmail=${encodeURIComponent(
        user?.email || ''
      )}`
    )
  }

  if (error) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Users
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            Error Loading User
          </Typography>
          <Typography>{error.message}</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  if (loading) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Users
        </BackButton>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (!user) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Users
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            User Not Found
          </Typography>
          <Typography>User with ID {userId} not found</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  const formatDate = (date: string | Date | null | undefined) =>
    date ? dayjs(date).format('MMM D, YYYY h:mm A') : 'N/A'

  const getUserType = () => {
    const isFunder = user.funderOrgs && user.funderOrgs.length > 0
    const isNonprofit = user.nonprofitOrgs && user.nonprofitOrgs.length > 0

    if (isFunder && isNonprofit) return 'Both'
    if (isFunder) return 'Funder'
    if (isNonprofit) return 'Nonprofit'
    return 'None'
  }

  const getUserTypeColor = () => {
    const type = getUserType()
    switch (type) {
      case 'Both':
        return colors.resiliaPurple
      case 'Funder':
        return colors.resiliaBlue
      case 'Nonprofit':
        return colors.paletteOrange
      default:
        return colors.lightGrey
    }
  }

  return (
    <PageContainer maxWidth='lg'>
      <HeaderSection>
        <BackButton
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant='text'
        >
          Back to Users
        </BackButton>

        <TitleSection>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='flex-start'
            width='100%'
          >
            <Box>
              <Box display='flex' alignItems='center'>
                <Title>{`${user.firstName} ${user.lastName}`}</Title>
                <StatusChip
                  label={user.disabled ? 'Disabled' : 'Active'}
                  status={user.disabled ? 'disabled' : 'active'}
                  size='small'
                />
                {user.isTest && (
                  <StatusChip
                    label='Test'
                    status='test'
                    size='small'
                    icon={<BugReportIcon />}
                  />
                )}
                <StatusChip
                  label={getUserType()}
                  status='custom'
                  size='small'
                  customColor={getUserTypeColor()}
                />
              </Box>
              <Subtitle>
                <EmailIcon fontSize='small' style={{ marginRight: 4 }} />
                {user.email} • User ID: {user.id}
              </Subtitle>
            </Box>
            <Box>
              <Tooltip title='Impersonate this user'>
                <Button
                  variant='contained'
                  color='error'
                  size='small'
                  startIcon={<PersonAddIcon />}
                  onClick={handleImpersonate}
                  disabled={user.disabled || impersonateLoading}
                  style={{
                    textTransform: 'none',
                  }}
                >
                  {impersonateLoading ? 'Impersonating...' : 'Impersonate User'}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </TitleSection>
      </HeaderSection>

      {/* Statistics Cards */}
      <Grid container spacing={3} marginBottom={4}>
        <Grid item xs={12} sm={4}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <BusinessIcon fontSize='large' />
              </StatIcon>
              <StatValue>{user.nonprofitOrgs?.length || 0}</StatValue>
              <StatLabel>Nonprofits</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <AccountBalanceIcon fontSize='large' />
              </StatIcon>
              <StatValue>{user.funderOrgs?.length || 0}</StatValue>
              <StatLabel>Funders</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard>
            <CardContent>
              <StatIcon>
                <GroupIcon fontSize='large' />
              </StatIcon>
              <StatValue>{getUserType()}</StatValue>
              <StatLabel>User Type</StatLabel>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Basic Information */}
      <InfoCard>
        <CardContent>
          <SectionTitle>
            <InfoIcon />
            Basic Information
          </SectionTitle>
          <InfoGrid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Full Name</InfoLabel>
                <InfoValue>{`${user.firstName} ${user.lastName}`}</InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>
                  <EmailIcon fontSize='small' />
                  {user.email}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Phone</InfoLabel>
                <InfoValue>
                  {user.phone ? (
                    <>
                      <PhoneIcon fontSize='small' />
                      {user.phone}
                    </>
                  ) : (
                    'Not provided'
                  )}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  {user.disabled ? (
                    <>
                      <CancelIcon fontSize='small' color='error' />
                      Disabled
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon
                        fontSize='small'
                        style={{ color: colors.darkGreen }}
                      />
                      Active
                    </>
                  )}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Environment</InfoLabel>
                <InfoValue>
                  {user.isTest ? (
                    <>
                      <BugReportIcon fontSize='small' color='action' />
                      Test User
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon
                        fontSize='small'
                        style={{ color: colors.resiliaBlue }}
                      />
                      Production
                    </>
                  )}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem>
                <InfoLabel>Experience Range</InfoLabel>
                <InfoValue>
                  {user.nonprofitExperienceRange || 'Not specified'}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoItem>
                <InfoLabel>Created</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDate(user.createdAt)}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoItem>
                <InfoLabel>Last Updated</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDate(user.updatedAt)}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoItem>
                <InfoLabel>Last Login</InfoLabel>
                <InfoValue>
                  <CalendarIcon fontSize='small' />
                  {formatDate(user.lastLoggedInAt)}
                </InfoValue>
              </InfoItem>
            </Grid>
          </InfoGrid>
        </CardContent>
      </InfoCard>

      {/* Two column layout for related entities */}
      <Grid container spacing={3}>
        {/* Nonprofits Panel */}
        <Grid item xs={12} md={6}>
          <SectionTitle>
            <BusinessIcon />
            Associated Nonprofits ({user.nonprofitOrgs?.length || 0})
          </SectionTitle>
          <ScrollablePanel elevation={0}>
            {user.nonprofitOrgs && user.nonprofitOrgs.length > 0 ? (
              <List>
                {user.nonprofitOrgs.map((nonprofit) => (
                  <ListItem key={nonprofit?.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: colors.paletteOrange }}>
                        <BusinessIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        nonprofit?.name ? (
                          <Link
                            to={`/internal/self-serve/nonprofit-admin/${nonprofit.id}`}
                          >
                            {nonprofit.name}
                          </Link>
                        ) : (
                          'Unknown'
                        )
                      }
                      secondary={
                        <>
                          ID: {nonprofit?.id}
                          {nonprofit?.disabled && ' • Disabled'}
                          {nonprofit?.isTest && ' • Test'}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>
                <BusinessIcon
                  style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}
                />
                <Typography variant='body2'>
                  No nonprofits associated
                </Typography>
              </EmptyState>
            )}
          </ScrollablePanel>
        </Grid>

        {/* Funders Panel */}
        <Grid item xs={12} md={6}>
          <SectionTitle>
            <AccountBalanceIcon />
            Associated Funders ({user.funderOrgs?.length || 0})
          </SectionTitle>
          <ScrollablePanel elevation={0}>
            {user.funderOrgs && user.funderOrgs.length > 0 ? (
              <List>
                {user.funderOrgs.map((funder) => (
                  <ListItem key={funder?.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: colors.resiliaBlue }}>
                        <AccountBalanceIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        funder?.name ? (
                          <Link
                            to={`/internal/self-serve/funder-admin/${funder.id}`}
                          >
                            {funder.name}
                          </Link>
                        ) : (
                          'Unknown'
                        )
                      }
                      secondary={
                        <>
                          ID: {funder?.id}
                          {funder?.disabled && ' • Disabled'}
                          {funder?.isTest && ' • Test'}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState>
                <AccountBalanceIcon
                  style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}
                />
                <Typography variant='body2'>No funders associated</Typography>
              </EmptyState>
            )}
          </ScrollablePanel>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <ActionBar>
        <Button
          variant='outlined'
          color='primary'
          startIcon={<AccountBalanceIcon />}
          onClick={navigateToFunders}
          disabled={!user.funderOrgs || user.funderOrgs.length === 0}
        >
          View All Funders
        </Button>
        <Button
          variant='outlined'
          color='primary'
          startIcon={<BusinessIcon />}
          onClick={navigateToNonprofits}
          disabled={!user.nonprofitOrgs || user.nonprofitOrgs.length === 0}
        >
          View All Nonprofits
        </Button>
      </ActionBar>

      {/* Impersonate Confirmation Dialog */}
      <Dialog
        open={showImpersonateDialog}
        onClose={() => setShowImpersonateDialog(false)}
      >
        <DialogTitle>Confirm Impersonation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to impersonate {user.firstName}{' '}
            {user.lastName} ({user.email})? You will be logged in as this user
            and can perform actions on their behalf.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImpersonateDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmImpersonate}
            color='error'
            variant='contained'
          >
            Impersonate
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  )
}
